import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import ConfiguratorService from "@/services/ConfiguratorService";
import { ConfiguratorValue } from "@/interfaces/components/configurator/ConfiguratorValue";
import { formatFormData } from "@/helpers/FormDataHelper";
import OfferItem from "@/models/OfferItem";
import { RouteNames } from "@/enums/routes/RouteNames";
import { ActiveProductFormValueObject } from "@/interfaces/general/ActiveProductFormValueObject";
import { JsonAPIError } from "@/interfaces/general/JsonAPIError";
import { EventBus } from "@/helpers/EventBusHelper";
import { EventBusEvents } from "@/enums/global/EventBusEvents";
import { ProjectTabValues } from "@/enums/components/Project/ProjectTabValues";
import { ProductTypes } from "@/enums/global/ProductTypes";
import { LoadingOverlayHelper } from "@/helpers/LoadingOverlayHelper";
import { ChecklistField } from "@/interfaces/components/configurator/ChecklistField";
import router from "@/router";

/* === Props & Emits === */
interface Props {
  productFormValues?: ActiveProductFormValueObject | null;
  isEditMode: boolean;
  production?: boolean;
  connection?: number | null;
  checklistFieldErrorsExist: boolean;
  formFieldErrorsExist: boolean;
  offerItemId?: string | null;
  paymentTypeId?: string | null;
  offerItemHasErrors: boolean;
  checklistErrors: ChecklistField[];
  productFormId: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddOfferItem',
  props: {
    productFormValues: { default: null },
    isEditMode: { type: Boolean },
    production: { type: Boolean, default: false },
    connection: { default: null },
    checklistFieldErrorsExist: { type: Boolean },
    formFieldErrorsExist: { type: Boolean },
    offerItemId: { default: null },
    paymentTypeId: { default: null },
    offerItemHasErrors: { type: Boolean },
    checklistErrors: null,
    productFormId: null
  },
  emits: ["show-errors", EventBusEvents.validateFormFields],
  setup(__props: any, { emit }) {

const props = __props as { productFormValues: ActiveProductFormValueObject | null, isEditMode: boolean, production: boolean, connection: number | null, checklistFieldErrorsExist: boolean, formFieldErrorsExist: boolean, offerItemId: string | null, paymentTypeId: string | null, offerItemHasErrors: boolean, checklistErrors: ChecklistField[], productFormId: string };




const { proxy } = getCurrentInstance()!;

/* === Offer Creation State === */
const isAllowedToCreateOffer = ref(false);
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const eventFinishedCallback = ref<(() => void) | null>(null);
const eventFinished = ref(false);

const allowErrors = computed(() => (props.offerItemHasErrors ? false : null));

const errorsHaveValidWarranty = computed(() => {
  if (!props.checklistErrors?.length) {
    return true;
  }

  const invalidWarranty = props.checklistErrors.some(
    (checklist: ChecklistField) => {
      if (checklist.checklistStringField) {
        return !checklist.checklistStringField.validWarranty;
      }
    }
  );

  return !invalidWarranty;
});

/* === Offer Creation === */
async function addProductToOffer() {
  loadingOverlay.start();
  eventFinishedCallback.value = null;
  emit(EventBusEvents.validateFormFields);

  await new Promise<void>((resolve) => {
    eventFinishedCallback.value = resolve;

    if (eventFinished.value) {
      eventFinished.value = false;
      resolve();
    }
  });

  if (props.formFieldErrorsExist) {
    loadingOverlay.stop();
    return;
  }

  const projectId = router.currentRoute.query.projectId as string;
  const routeOfferId = router.currentRoute.query.offerId as string;
  let validOfferId;
  try {
    validOfferId = await ConfiguratorService.getValidOfferId(
      projectId,
      routeOfferId,
      props.paymentTypeId
    );
  } catch (e) {
    (proxy as any).$notification.error({
      message: (proxy as any).$t("Dogodila se greška") as string,
      description: (e as Error).message,
    });
    loadingOverlay.stop();
    return;
  }

  const formData: Array<{ id: string; value: ConfiguratorValue }> =
    formatFormData(props.productFormValues as ActiveProductFormValueObject);

  try {
    if (!props.isEditMode) {
      // tslint:disable-next-line:max-line-length
      await OfferItem.createNew(
        formData,
        String(validOfferId),
        props.checklistFieldErrorsExist,
        props.connection,
        props.production,
        errorsHaveValidWarranty.value,
        props.productFormId
      );
    } else {
      await OfferItem.updateExisting(
        formData,
        String(props.offerItemId),
        ProductTypes.PRODUCT,
        props.checklistFieldErrorsExist,
        props.productFormId,
        props.connection,
        props.production,
        allowErrors.value,
        errorsHaveValidWarranty.value
      );
    }
  } catch (e) {
    (e as any).response.data.errors.forEach((error: JsonAPIError) => {
      (proxy as any).$notification.error({
        message: (proxy as any).$t("Dogodila se greška") as string,
        description: error.title ? error.title : (e as any).message,
      });
    });
    loadingOverlay.stop();
    return;
  }

  EventBus.$emit(EventBusEvents.changesInDataMade, { state: false });

  router.push({
    name: RouteNames.project,
    params: { id: projectId },
    query: {
      offerId: validOfferId,
      initialTab: `${ProjectTabValues.Products}`,
    },
  });
  loadingOverlay.stop();
}

onMounted(() => {
  if (router.currentRoute.query.projectId != null) {
    isAllowedToCreateOffer.value = true;
  }
});

/* === Form Validation Events === */
function continueExecution() {
  if (eventFinishedCallback.value) {
    eventFinishedCallback.value();
  } else {
    eventFinished.value = true;
  }

  eventFinishedCallback.value = null;
}

EventBus.$on(EventBusEvents.formValidationFinished, continueExecution);

onBeforeUnmount(() => {
  EventBus.$off(EventBusEvents.formValidationFinished, continueExecution);
});

return { __sfc: true,props, emit, proxy, isAllowedToCreateOffer, loadingOverlay, eventFinishedCallback, eventFinished, allowErrors, errorsHaveValidWarranty, addProductToOffer, continueExecution }
}

})